import { createContext, PropsWithChildren, useContext, useState } from 'react';
import { TableState } from 'react-table';
import { ContractReviewGridItem, SortableDraftContractFields } from 'shared/types';
import { useQueryDraftContracts } from 'shared/queries';
import { GetDraftContractsResponse } from 'shared/services';

export interface ContractApprovalWorkflowTableFilters {
  showTermOrTierChanges: boolean;
  showDeletionsOrRestorations: boolean;
}

const defaultState: TableState<ContractReviewGridItem> = {
  selectedRowIds: {},
  pageIndex: 0,
  pageSize: 100,
  globalFilter: '',
  filters: [],
  sortBy: [],
  hiddenColumns: [],
};

export const defaultFilters: ContractApprovalWorkflowTableFilters = {
  showDeletionsOrRestorations: true,
  showTermOrTierChanges: true,
};

export interface ContractApprovalWorkflowTableState {
  tableState: TableState<ContractReviewGridItem>;
  tableFilters: ContractApprovalWorkflowTableFilters;
}

interface ContractApprovalWorkflowListContext {
  contractApprovalWorkflowTableState: ContractApprovalWorkflowTableState;
  setContractApprovalWorkflowTableState: React.Dispatch<React.SetStateAction<ContractApprovalWorkflowTableState>>;
  tableFilters: ContractApprovalWorkflowTableFilters;
  setTableFilters: React.Dispatch<React.SetStateAction<ContractApprovalWorkflowTableFilters>>;
  formFilters: ContractApprovalWorkflowTableFilters;
  setFormSearchFilters: React.Dispatch<React.SetStateAction<ContractApprovalWorkflowTableFilters>>;
  draftContracts: GetDraftContractsResponse | undefined;
  isFetching: boolean;
  isLoading: boolean;
}
const contractApprovalWorkflowListContext = createContext({} as ContractApprovalWorkflowListContext);

export function ProvideContractApprovalWorkflowListState({ children }: PropsWithChildren<{}>) {
  const [tableFilters, setTableFilters] = useState(defaultFilters);
  const [formSearchFilters, setFormSearchFilters] = useState<ContractApprovalWorkflowTableFilters>(tableFilters);
  const [
    contractApprovalWorkflowTableState,
    setContractApprovalWorkflowTableState,
  ] = useState<ContractApprovalWorkflowTableState>({
    tableState: defaultState,
    tableFilters,
  });

  const { draftContracts, isFetching, isLoading } = useQueryDraftContracts({
    showTermOrTierChanges: contractApprovalWorkflowTableState.tableFilters.showTermOrTierChanges,
    showDeletionsOrRestorations: contractApprovalWorkflowTableState.tableFilters.showDeletionsOrRestorations,
    page: contractApprovalWorkflowTableState.tableState.pageIndex,
    pageSize: contractApprovalWorkflowTableState.tableState.pageSize,
    sortOrder: !!contractApprovalWorkflowTableState.tableState.sortBy[0]
      ? contractApprovalWorkflowTableState.tableState.sortBy[0].desc
        ? 'desc'
        : 'asc'
      : undefined,
    sortBy: !!contractApprovalWorkflowTableState.tableState.sortBy[0]
      ? (contractApprovalWorkflowTableState.tableState.sortBy[0].id as SortableDraftContractFields)
      : undefined,
  });

  return (
    <contractApprovalWorkflowListContext.Provider
      value={{
        contractApprovalWorkflowTableState,
        setContractApprovalWorkflowTableState,
        tableFilters,
        setTableFilters,
        formFilters: formSearchFilters,
        setFormSearchFilters,
        draftContracts,
        isFetching,
        isLoading,
      }}
    >
      {children}
    </contractApprovalWorkflowListContext.Provider>
  );
}

export function useContractApprovalWorkflowListState() {
  return useContext(contractApprovalWorkflowListContext);
}
